import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Card from '@/components/card';
import Title from '@/components/title';
import { ISearchTaxInvoiceReportReq, ITaxInvoice } from '@/interfaces/report';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker, TablePaginationConfig } from 'antd';
import locale from 'antd/es/date-picker/locale/th_TH';
import { ReportMenu } from '@/configs/menuItems';
import { getPathSegment } from '@/utils/pathUtils';
import SubMenu from '@/components/menu/sub-menu';
import SalesReportTable from './components/sales-report-table';
// import Search from 'antd/es/input/Search';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { AxiosResponse } from 'axios';
import { searchTaxInvoiceReport } from '@/adapters/report';
import BusinessUnitSelector from '@/components/selector/business-unit-selector';
import CustomerSelector from '@/components/selector/customer-selector';
import { ICustomer } from '@/interfaces/customer';
import ExcelExport from './components/export-report';
import PDFExport from './components/export-pdf-report';
import { useAuth } from '@/contexts/AuthContext';

const TaxInvoiceReportReport = () => {
  const pathSegment = getPathSegment();
  const { userInfo } = useAuth();
  const [criteria, setCriteria] = useState<ISearchTaxInvoiceReportReq>({
    startDate: dayjs().startOf('month').toDate(),
    endDate: dayjs().endOf('month').toDate(),
    pageSize: 10,
    pageStart: 1,
    customerId: null,
    businessUnitId: userInfo?.bu || undefined,
  });

  const { data, isLoading } = useQuery<AxiosResponse<ITaxInvoice[]>, Error>({
    queryKey: ['search-sales-report', criteria],
    queryFn: async () => await searchTaxInvoiceReport(criteria),
  });

  const onTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<ITaxInvoice> | SorterResult<ITaxInvoice>[],
  ) => {
    const newCriteria = { ...criteria };
    if (!Array.isArray(sorter)) {
      newCriteria.orderBy =
        (sorter.column?.dataIndex as keyof ITaxInvoice) || '';
      newCriteria.orderType = sorter.order || '';
    }
    setCriteria({
      ...newCriteria,
      pageStart: Number(pagination.current || 1),
      pageSize: Number(pagination.pageSize || 10),
    });
  };

  const onRangeChange = (date: Dayjs) => {
    if (date) {
      setCriteria({
        ...criteria,
        orderBy: 'id',
        orderType: '',
        pageStart: 1,
        pageSize: 10,
        startDate: dayjs(date).startOf('month').toDate(),
        endDate: dayjs(date).endOf('month').toDate(),
      });
    } else {
      setCriteria({
        ...criteria,
        orderBy: 'id',
        orderType: '',
        pageStart: 1,
        pageSize: 10,
        startDate: dayjs().startOf('month').toDate(),
        endDate: dayjs(date).endOf('month').toDate(),
      });
    }
  };

  const onCustomerChange = (customerInfo: ICustomer) => {
    setCriteria({
      ...criteria,
      orderBy: 'id',
      orderType: '',
      pageStart: 1,
      pageSize: 10,
      customerId: customerInfo?.id,
    });
  };

  return (
    <>
      <SubMenu active={pathSegment} items={ReportMenu} />
      <Card customClass="min-h-[200px]">
        <Title
          title={`รายงานภาษีขาย ประจำเดือน ${dayjs(criteria.startDate).format('MMMM YYYY')}`}
        >
          <div className="flex flex-row gap-2">
            <ExcelExport data={data?.data || []} date={criteria?.endDate} />
            <PDFExport data={data?.data || []} date={criteria?.endDate} />
          </div>
        </Title>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col md:flex-row justify-between gap-3">
            <div className="flex flex-col md:flex-row justify-between gap-3">
              <DatePicker.MonthPicker
                size="large"
                locale={locale}
                style={{ minWidth: 150 }}
                defaultValue={dayjs()}
                onChange={onRangeChange}
              />
              <BusinessUnitSelector
                isAll
                handleSelectData={id => {
                  setCriteria({
                    ...criteria,
                    businessUnitId: id,
                    pageStart: 1,
                  });
                }}
                disabled={!!userInfo?.bu}
                value={criteria.businessUnitId}
              />
              <CustomerSelector
                handleSelectData={onCustomerChange}
                allowClear={true}
                customClass="md:min-w-[300px]"
              />
            </div>
          </div>
          <SalesReportTable
            criteria={criteria}
            report={{
              data: data?.data ?? [],
              totalRecords: data?.data?.length || 0,
            }}
            loading={isLoading}
            onTableChange={onTableChange}
          />
        </div>
      </Card>
    </>
  );
};

export default TaxInvoiceReportReport;
